import { isEqual } from 'lodash';
import { FieldMetaProps } from 'formik';
import { useParams } from 'react-router-dom';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import useDeclarations from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import { DeclarationFieldMeta, DeclarationMetaSource } from 'store/declarations/declaration-source-meta';

/** Product arrays, each followed by the item's index. */
const PRODUCT_CONTAINERS = [
    'cdsGovernmentAgencyGoodsItems',
    'goodsShipmentItem',
    'governmentAgencyGoodsItem',
    'goodsItems',
];

/** UK wraps each product in another object; product form paths start below that wrapper. */
const ITEM_WRAPPERS = ['governmentAgencyGoodsItem'];

const DeclarationSourceContext = createContext<{ fieldSources?: Record<string, DeclarationFieldMeta> }>({});

interface Props {
    children: ReactNode;
    /**
     * The branch of the declaration the master form is built from, so its field paths start below it.
     * Comes from the declaration map as a prop: importing that map here would close an import cycle
     * back through the form blocks that use these inputs.
     */
    payloadPath?: string;
}

/** Translates the declaration-wide metadata paths into paths of the form currently on screen. */
const DeclarationSourceContextProvider = ({ children, payloadPath }: Props) => {
    const { declaration } = useDeclarations();
    const { productId } = useParams<{ productId: string }>();
    const { products } = useProducts({ productId });

    /** Metadata indices count across the declaration, while the products list holds a single page of it. */
    const productIndex = useMemo(() => {
        if (!productId) return undefined;

        const list = (products?.list ?? []) as { id?: string }[];
        const index = list.findIndex((item) => item.id === productId);
        if (index === -1) return undefined;

        return (products?.pageNumber ?? 0) * (products?.pageSize ?? 0) + index;
    }, [productId, products?.list, products?.pageNumber, products?.pageSize]);

    const meta = declaration?.meta;

    const fieldSources = useMemo(() => {
        if (!meta) return undefined;

        return Object.entries(meta).reduce<Record<string, DeclarationFieldMeta>>((acc, [path, field]) => {
            if (!field?.source) return acc;

            const { formPath, productIndex: fieldProductIndex } = parsePath(path, payloadPath);

            // The master form shows only its own fields; a product form shows only the product it opened.
            if (!formPath || fieldProductIndex !== (productId ? productIndex : undefined)) return acc;

            acc[formPath] = field;

            return acc;
        }, {});
    }, [meta, productId, productIndex, payloadPath]);

    return <DeclarationSourceContext.Provider value={{ fieldSources }}>{children}</DeclarationSourceContext.Provider>;
};

export default DeclarationSourceContextProvider;

export interface ResolvedFieldSource {
    source: DeclarationMetaSource;
    overridden: boolean;
}

/** Resolves where a field's value came from, and whether the user has since changed it. */
export const useDeclarationValueSource = (
    fieldPath?: string,
    fieldMeta?: FieldMetaProps<any>
): ResolvedFieldSource | undefined => {
    const { fieldSources } = useContext(DeclarationSourceContext);
    const { value, initialValue } = fieldMeta ?? {};

    return useMemo(() => {
        const field = fieldPath ? fieldSources?.[fieldPath] : undefined;
        if (!field?.source) return undefined;

        return { source: field.source, overridden: !!field.updated || isEdited(value, initialValue) };
    }, [fieldPath, fieldSources, value, initialValue]);
};

/**
 * `updated` only covers saved edits, so unsaved ones are caught by comparing against the value the
 * declaration was loaded with — which also clears the indicator when an edit is undone.
 */
const isEdited = (value: unknown, initialValue: unknown) => !isEqual(normalise(value), normalise(initialValue));

const normalise = (value: unknown) => {
    if (value === null || value === undefined || value === '') return undefined;

    return typeof value === 'object' ? value : String(value);
};

/**
 * Trims a metadata path down to its form path — `…GoodsItems.38.governmentAgencyGoodsItem.origin.1.countryCode`
 * becomes `origin.1.countryCode` on product 38. Products are recognised by their container rather than by
 * the first index, because master forms hold indexed lists of their own.
 */
const parsePath = (path: string, payloadPath?: string) => {
    const segments = path.split('.');
    const containerIndex = segments.findIndex(
        (segment, index) => PRODUCT_CONTAINERS.includes(segment) && isIndex(segments[index + 1])
    );

    // Master field: drop the declaration root key, e.g. `cdsDeclaration`, then the master payload branch.
    if (containerIndex === -1) {
        const rest = segments.slice(1);
        const payloadSegments = payloadPath ? payloadPath.split('.') : [];

        // Outside that branch the field isn't on the master form, and its path would shadow one that is.
        if (!isEqual(rest.slice(0, payloadSegments.length), payloadSegments))
            return { formPath: undefined, productIndex: undefined };

        return { formPath: rest.slice(payloadSegments.length).join('.'), productIndex: undefined };
    }

    const rest = segments.slice(containerIndex + 2);
    if (ITEM_WRAPPERS.includes(rest[0])) rest.shift();

    return { formPath: rest.join('.'), productIndex: Number(segments[containerIndex + 1]) };
};

const isIndex = (segment?: string) => !!segment && /^\d+$/.test(segment);
